// src/components/admin/act/ACTManagement.js
import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../firebaseConfig';
import { collection, doc, writeBatch, onSnapshot, query, orderBy } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import Papa from 'papaparse';

const styles = {
  container: { padding: '24px', maxWidth: '1400px', margin: '0 auto', height: 'calc(100vh - 100px)' },
  grid: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px', height: '100%', alignItems: 'start' },
  sidebar: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', height: '100%', display: 'flex', flexDirection: 'column' },
  sidebarHeader: { padding: '16px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, display: 'flex', justifyContent: 'space-between' },
  testList: { flex: 1, overflowY: 'auto' },
  testItem: (active) => ({
    padding: '14px 16px', cursor: 'pointer',
    background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#4b5563',
    borderLeft: active ? '4px solid #2563eb' : '4px solid transparent',
    borderBottom: '1px solid #f3f4f6', fontWeight: active ? 600 : 400
  }),
  content: { height: '100%', display: 'flex', flexDirection: 'column', gap: '20px', overflow: 'hidden' },
  card: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' },
  formRow: { display: 'flex', gap: '12px', alignItems: 'flex-end', marginTop: 10 },
  input: { flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db' },
  btn: { padding: '9px 20px', background: '#111827', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 },
  
  // List Styles
  qList: { flex: 1, background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflowY: 'auto' },
  qRow: { display: 'flex', gap: '16px', padding: '16px', borderBottom: '1px solid #f3f4f6' },
  imgBox: { width: 120, height: 80, background: '#f9fafb', border: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  img: { maxWidth: '100%', maxHeight: '100%' },
  badge: { fontSize: 11, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, marginRight: 6 }
};

// Helper to show images safely
const SafeImage = ({ path }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if(!path) return;
    getDownloadURL(ref(storage, path)).then(setUrl).catch(e => console.log("Img err:", path));
  }, [path]);
  if(!url) return <span style={{fontSize:10, color:'#ccc'}}>No Img</span>;
  return <img src={url} alt="Content" style={styles.img} />;
};

export default function ACTManagement() {
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [scoreFile, setScoreFile] = useState(null);
  const [scoreSubject, setScoreSubject] = useState('English');
  
  // Upload State
  const [newTestId, setNewTestId] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState(false);

  // 1. Load Existing ACT Tests
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "act_diagnostic_tests"), (snap) => {
      setTests(snap.docs.map(d => d.id));
    });
    return () => unsub();
  }, []);

  // 2. Load Questions for Selected Test
  useEffect(() => {
    if (!selectedTestId) return;
    const qQuery = query(collection(db, "act_diagnostic_tests", selectedTestId, "questions"), orderBy("questionId")); // Ordered by ID naturally sorts Q1, Q10... can fix later
    const unsub = onSnapshot(qQuery, (snap) => {
      setQuestions(snap.docs.map(d => d.data()));
    });
    return () => unsub();
  }, [selectedTestId]);

  const handleUpload = () => {
    if (!newTestId || !csvFile) return alert("Enter ID and CSV");
    setUploading(true);
    setStatus('Parsing CSV...');

    Papa.parse(csvFile, {
      header: true, skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        setStatus(`Uploading ${rows.length} questions...`);
        
        try {
          let batch = writeBatch(db);
          let writes = 0;

          // Create Parent Doc
          batch.set(doc(db, "act_diagnostic_tests", newTestId), { 
            testId: newTestId, 
            createdAt: new Date(),
            type: 'ACT' 
          });

          for (const row of rows) {
            // Expected CSV Headers: Subject, Question Number, Passage, Answer, Category
            if (!row.Subject || !row['Question Number']) continue;

            const subject = row.Subject.trim(); // English, Math, Reading, Science
            const qNum = row['Question Number'].trim();
            const passageName = row.Passage ? row.Passage.trim() : null;
            
            // Construct IDs and Paths based on your specific format
            // ID: English_Q1
            const uniqueId = `${subject}_Q${qNum}`; 
            
            // Question Img: act_diagnostic/TEST_ID/question/English_Q1.png
            const qImgPath = `act_diagnostic/${newTestId}/question/${uniqueId}.png`;

            // Passage Img: act_diagnostic/TEST_ID/passage/English_Passage_1.png
            // (Only if passage column is not empty)
            const pImgPath = passageName 
              ? `act_diagnostic/${newTestId}/passage/${passageName}.png` 
              : '';

            const qRef = doc(db, "act_diagnostic_tests", newTestId, "questions", uniqueId);
            
            batch.set(qRef, {
              questionId: uniqueId,
              subject: subject,
              questionNumber: Number(qNum),
              correctAnswer: row.Answer || '',
              category: row.Category || '',
              passageName: passageName || '',
              questionImagePath: qImgPath,
              passageImagePath: pImgPath
            });

            writes++;
            if (writes >= 400) { await batch.commit(); batch = writeBatch(db); writes = 0; }
          }
          
          if (writes > 0) await batch.commit();
          
          setStatus('Done!');
          setNewTestId(''); setCsvFile(null); setSelectedTestId(newTestId);
        } catch (err) {
          console.error(err);
          setStatus('Error: ' + err.message);
        } finally {
          setUploading(false);
        }
      }
    });
  };

  const handleScoreUpload = () => {
    if (!selectedTestId || !scoreFile) return alert("Select a test and a CSV file.");
    
    setUploading(true);
    setStatus(`Parsing ${scoreSubject} scores...`);

    Papa.parse(scoreFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data;
          const batch = writeBatch(db);
          // Collection name: e.g., English_scores, Math_scores
          const collectionName = `${scoreSubject}_scores`; 
          
          let count = 0;
          
          rows.forEach(row => {
            // Find keys dynamically (e.g., "English Raw Score", "Scale Score")
            const keys = Object.keys(row);
            const rawKey = keys.find(k => k.toLowerCase().includes('raw'));
            const scaleKey = keys.find(k => k.toLowerCase().includes('scale'));

            if (rawKey && scaleKey) {
              const rawVal = row[rawKey].trim();
              const scaleVal = Number(row[scaleKey].trim());

              // Doc ID is the Raw Score (for easy lookup later)
              const ref = doc(db, "act_diagnostic_tests", selectedTestId, collectionName, rawVal);
              batch.set(ref, { raw: Number(rawVal), scale: scaleVal });
              count++;
            }
          });

          await batch.commit();
          setStatus(`Success! Uploaded ${count} ${scoreSubject} conversion rows.`);
          setScoreFile(null);
        } catch (err) {
          console.error(err);
          setStatus("Error uploading scores: " + err.message);
        } finally {
          setUploading(false);
        }
      }
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        {/* Sidebar */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <span>ACT Tests</span>
          </div>
          <div style={styles.testList}>
            {tests.map(id => (
              <div key={id} style={styles.testItem(selectedTestId === id)} onClick={() => setSelectedTestId(id)}>
                {id}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Uploader */}
          <div style={styles.card}>
            <h3>Create / Update ACT Test</h3>
            <div style={styles.formRow}>
              <input 
                placeholder="Test ID (e.g. act_diagnostic_test1)" 
                value={newTestId}
                onChange={e => setNewTestId(e.target.value)}
                style={styles.input}
              />
              <input 
                type="file" 
                accept=".csv"
                onChange={e => setCsvFile(e.target.files[0])}
                style={styles.input}
              />
              <button onClick={handleUpload} disabled={uploading} style={styles.btn}>
                {uploading ? 'Processing...' : 'Upload CSV'}
              </button>
            </div>
            {status && <div style={{marginTop:10, color:'blue'}}>{status}</div>}
          </div>

          {/* SCORING UPLOAD CARD */}
          <div style={{...styles.card, marginTop: 20}}>
            <h3>Upload Scoring Table (Raw → Scale)</h3>
            <div style={{fontSize:13, color:'#666', marginBottom:10}}>
              Select a subject and upload a CSV with headers like "Raw Score" and "Scale Score".
              <br/>Make sure a Test ID is selected in the sidebar first.
            </div>
            
            <div style={styles.formRow}>
              <select 
                value={scoreSubject} 
                onChange={e => setScoreSubject(e.target.value)}
                style={{...styles.input, flex: '0 0 150px'}}
              >
                <option value="English">English</option>
                <option value="Math">Math</option>
                <option value="Reading">Reading</option>
                <option value="Science">Science</option>
              </select>

              <input 
                type="file" 
                accept=".csv"
                onChange={e => setScoreFile(e.target.files[0])}
                style={styles.input}
              />

              <button 
                onClick={handleScoreUpload} 
                disabled={uploading || !selectedTestId} 
                style={{...styles.btn, opacity: (!selectedTestId) ? 0.5 : 1}}
              >
                {uploading ? 'Uploading...' : 'Upload Scale'}
              </button>
            </div>
          </div>

          {/* List View */}
          {selectedTestId && (
            <div style={styles.qList}>
              <div style={{padding:16, fontWeight:700, borderBottom:'1px solid #eee'}}>
                Questions in {selectedTestId} ({questions.length})
              </div>
              {questions.map(q => (
                <div key={q.questionId} style={styles.qRow}>
                  {/* Passage Image Preview (Left) */}
                  <div style={{width: 100}}>
                    <div style={{fontSize:10, marginBottom:4}}>Passage</div>
                    <div style={styles.imgBox}>
                      <SafeImage path={q.passageImagePath} />
                    </div>
                  </div>

                  {/* Question Image Preview (Center) */}
                  <div style={{width: 100}}>
                    <div style={{fontSize:10, marginBottom:4}}>Question</div>
                    <div style={styles.imgBox}>
                      <SafeImage path={q.questionImagePath} />
                    </div>
                  </div>

                  {/* Info */}
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700}}>{q.questionId}</div>
                    <div style={{margin:'4px 0'}}>
                      <span style={styles.badge}>{q.subject}</span>
                      <span style={styles.badge}>Ans: {q.correctAnswer}</span>
                    </div>
                    <div style={{fontSize:12, color:'#666'}}>
                      Passage: {q.passageName || 'None'} <br/>
                      Path: {q.questionImagePath}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}